import { Group, GroupMember, RawGroup, RawUser, User } from "@/types/users";

import { WEBMSG_ENDPOINT } from "@/constants/links";
import api from "./api";

const USERS_CACHE_TTL_MS = 60_000;
// GetGroupsForOrg (cheap: just names/ids/addrs, no owner info) and a healthy per-group
// member snapshot share the same freshness window.
const GROUPS_LIST_TTL_MS = 60_000;
const MEMBERS_TTL_MS = 60_000;
// A group whose members couldn't be resolved (fetch failed / came back empty) is cached
// only briefly, so it retries soon — WITHOUT re-firing every other group's member fetch.
const MEMBERS_DEGRADED_TTL_MS = 10_000;

let usersCache: { users: User[]; fetchedAt: number } | null = null;
let usersInFlight: Promise<User[]> | null = null;

/**
 * Clears cached org user list. Call on logout or when org membership may have changed.
 */
export function invalidateOrgUsersCache(): void {
  usersCache = null;
  usersInFlight = null;
}

async function requestUsersFromApi(): Promise<User[]> {
  const payload = {
    cmd: "GetUsersForOrg",
  };

  const response = await api.post(WEBMSG_ENDPOINT, payload);

  const formatted: User[] = [];
  response.data.users.forEach((user: RawUser) => {
    const formattedUser: User = {
      username: user.name,
      firstname: user.first,
      lastname: user.last,
      id: user.id,
    };
    formatted.push(formattedUser);
  });

  return formatted;
}

/**
 * Retrieves users for the organization. Sends POST request to get the users in the org
 * and formats the response data into an array of User objects.
 *
 * Results are deduped while in-flight and cached briefly to avoid redundant calls when
 * multiple components mount (e.g. chat UI + encryption).
 *
 * @returns {Promise<User[]>} A promise that resolves with an array of User objects.
 * @throws {Error} Throws an error if the request fails.
 */
export async function getUsers(options?: {
  force?: boolean;
}): Promise<User[]> {
  if (options?.force) {
    usersCache = null;
  }

  if (
    !options?.force &&
    usersCache &&
    Date.now() - usersCache.fetchedAt < USERS_CACHE_TTL_MS
  ) {
    return usersCache.users;
  }

  if (usersInFlight) {
    return usersInFlight;
  }

  usersInFlight = (async () => {
    try {
      const users = await requestUsersFromApi();
      usersCache = { users, fetchedAt: Date.now() };
      return users;
    } finally {
      usersInFlight = null;
    }
  })();

  return usersInFlight;
}

/**
 * Clears all cached group state (raw list + per-group members).
 * Call on logout or when group membership may have changed.
 */
export function invalidateGroupsCache(): void {
  rawGroupsCache = null;
  rawGroupsInFlight = null;
  groupMembersCache.clear();
  groupMembersInFlight.clear();
}

// --- Group list (GetGroupsForOrg): names/ids/addrs only, no owner info. ---
let rawGroupsCache: { raw: RawGroup[]; fetchedAt: number } | null = null;
let rawGroupsInFlight: Promise<RawGroup[]> | null = null;

async function getRawGroups(force: boolean): Promise<RawGroup[]> {
  if (force && rawGroupsInFlight) {
    // A pre-force fetch is running; wait for it to settle, then start a genuinely fresh
    // one so the result reflects state as of *now* (its finally nulls rawGroupsInFlight).
    return rawGroupsInFlight.catch(() => {}).then(() => getRawGroups(true));
  }
  if (!force) {
    if (rawGroupsCache && Date.now() - rawGroupsCache.fetchedAt < GROUPS_LIST_TTL_MS) {
      return rawGroupsCache.raw;
    }
    if (rawGroupsInFlight) return rawGroupsInFlight;
  } else {
    rawGroupsCache = null;
  }

  rawGroupsInFlight = (async () => {
    try {
      const response = await api.post(WEBMSG_ENDPOINT, { cmd: "GetGroupsForOrg" });
      const raw: RawGroup[] = response.data?.groups ?? [];
      rawGroupsCache = { raw, fetchedAt: Date.now() };
      return raw;
    } finally {
      rawGroupsInFlight = null;
    }
  })();
  return rawGroupsInFlight;
}

// --- Per-group members (GetGroupMembers): the source of ownership (each member carries an
// `isOwner` flag). Cached per group id so a single group's failure short-TTLs only itself,
// and the periodic list refresh reuses healthy member snapshots instead of re-fanning the
// whole org's members every time. ---
type MembersEntry = { members: GroupMember[]; fetchedAt: number; degraded: boolean };
const groupMembersCache = new Map<number, MembersEntry>();
const groupMembersInFlight = new Map<number, Promise<GroupMember[]>>();

async function fetchGroupMembers(groupId: number): Promise<GroupMember[]> {
  const response = await api.post(WEBMSG_ENDPOINT, { cmd: "GetGroupMembers", groupId });
  return Array.isArray(response.data) ? response.data : [];
}

async function getGroupMembersCached(
  groupId: number,
  groupName: string,
  force: boolean
): Promise<GroupMember[]> {
  const inFlight = groupMembersInFlight.get(groupId);
  if (force && inFlight) {
    return inFlight.catch(() => {}).then(() => getGroupMembersCached(groupId, groupName, true));
  }
  const cached = groupMembersCache.get(groupId);
  if (!force && cached) {
    const ttl = cached.degraded ? MEMBERS_DEGRADED_TTL_MS : MEMBERS_TTL_MS;
    if (Date.now() - cached.fetchedAt < ttl) return cached.members;
  }
  if (!force && inFlight) return inFlight;

  const promise = (async () => {
    let members: GroupMember[] = [];
    let degraded = false;
    try {
      members = await fetchGroupMembers(groupId);
      // A group always has at least an owner; an empty list means we couldn't resolve
      // ownership (unexpected 200 shape) — treat it as degraded so it retries soon.
      if (members.length === 0) degraded = true;
    } catch (err) {
      console.error(`[Groups] Failed to fetch members for group "${groupName}":`, err);
      degraded = true;
    } finally {
      groupMembersInFlight.delete(groupId);
    }
    // On a transient failure keep serving the last healthy snapshot (so ownerName stays
    // resolved and E2EE keeps working), but store it degraded so the short TTL retries it.
    if (degraded && cached && !cached.degraded && cached.members.length > 0) {
      members = cached.members;
    }
    groupMembersCache.set(groupId, { members, fetchedAt: Date.now(), degraded });
    return members;
  })();
  groupMembersInFlight.set(groupId, promise);
  return promise;
}

/**
 * Retrieves groups for the organization, enriched with owner/member info.
 *
 * The cheap group list (GetGroupsForOrg) and each group's members (GetGroupMembers) are
 * cached independently and deduped while in-flight, so hot-path callers (e.g. switching
 * conversations, incoming group messages) rarely hit the network, and a single failing
 * group doesn't force the whole 1+N fan-out to re-fire.
 *
 * @returns {Promise<Group[]>} A promise that resolves to an array of Group objects.
 * @throws {Error} Throws if the group list fetch itself fails.
 */
export async function getGroups(options?: { force?: boolean }): Promise<Group[]> {
  const force = options?.force ?? false;
  const raw = await getRawGroups(force);

  return Promise.all(
    raw.map(async (group: RawGroup): Promise<Group> => {
      const members = await getGroupMembersCached(group.id, group.name, force);
      const ownerName = members.find((m) => m.isOwner)?.username ?? "";
      return {
        address: group.addr,
        description: group.description,
        name: group.name,
        id: group.id,
        ownerName,
        members,
      };
    })
  );
}
